import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Megaphone, Plus, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useUndo,
  useConfirm,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { useMe } from '@/auth/useMe';
import { formatDate } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useProductLists } from '@/features/products/admin/api';
import { lastMonthKey, useAwards, useAwardTypes, useCandidates, useGamesMutations } from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * מצטייני החודש (PRD 5.8.5).
 *
 * המערכת מציעה מועמדים לפי נקודות ולומדות, וההנהלה בוחרת וכותבת
 * סיבה. ההצעה היא עזר ולא החלטה: יש דברים שהמערכת לא רואה.
 */
export function AwardsPage() {
  const confirm = useConfirm();
  const canManage = usePerm('games.manage');
  const undo = useUndo();
  const { me } = useMe();
  const [month, setMonth] = useState(lastMonthKey());
  const awards = useAwards();
  const types = useAwardTypes();
  const candidates = useCandidates(month);
  const lists = useProductLists();
  const mutations = useGamesMutations();
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  const channels = lists.data?.channels ?? [];

  return (
    <AdminPage title="מצטיינים" subtitle="מי קיבל מה, ולמה">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <Panel title="בחירת מצטיין">
        <form
          className="grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const typeId = String(form.get('type') ?? '');
            const who = String(form.get('who') ?? '');
            if (!typeId || !who || !me?.employeeId) return;

            const type = types.data?.find((t) => t.id === typeId);
            run(() =>
              mutations.createAward.mutateAsync({
                award_type_id: typeId,
                employee_id: type?.target === 'branch' ? null : who,
                branch_id: type?.target === 'branch' ? who : null,
                period_month: month,
                reason: String(form.get('reason') ?? '').trim() || null,
                created_by: me.employeeId,
              }),
            );
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1">
              <label htmlFor="month" className="text-body-sm text-muted">
                לאיזה חודש
              </label>
              <input
                id="month"
                type="month"
                value={month.slice(0, 7)}
                onChange={(event) => setMonth(`${event.target.value}-01`)}
                className={FIELD}
              />
            </div>
            <div className="grid gap-1">
              <label htmlFor="type" className="text-body-sm text-muted">
                סוג הפרס
              </label>
              <select id="type" name="type" className={FIELD} required>
                <option value="">בחירה</option>
                {types.data?.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                    {type.default_points > 0 && ` (${type.default_points} נקודות)`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-1">
            <label htmlFor="who" className="text-body-sm text-muted">
              מי זוכה
            </label>
            <select id="who" name="who" className={FIELD} required>
              <option value="">בחירה</option>
              <optgroup label="עובדים">
                {candidates.data?.employees.map((c) => (
                  <option key={c.employee_id} value={c.employee_id}>
                    {c.name} — {c.points} נקודות, {c.courses} לומדות
                  </option>
                ))}
              </optgroup>
              <optgroup label="סניפים">
                {candidates.data?.branches.map((b) => (
                  <option key={b.branch_id} value={b.branch_id}>
                    {b.name} — {b.points} נקודות
                  </option>
                ))}
              </optgroup>
            </select>
            <p className="m-0 text-label text-muted">
              הרשימה מסודרת לפי מה שהמערכת רואה. הבחירה היא שלך.
            </p>
          </div>

          <div className="grid gap-1">
            <label htmlFor="reason" className="text-body-sm text-muted">
              הסיבה, כפי שתופיע בהכרזה
            </label>
            <textarea id="reason" name="reason" rows={2} className={`${FIELD} py-2`} />
          </div>

          <div>
            <Button variant="primary" type="submit" disabled={mutations.createAward.isPending}>
              <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
              שמירה כטיוטה
            </Button>
          </div>
          <p className="m-0 text-body-sm text-muted">
            ההכרזה בערוץ היא כפתור נפרד ברשימה, והיא גם מזכה בנקודות.
          </p>
        </form>
      </Panel>

      {awards.isPending && <Skeleton className="h-40" />}
      {awards.isError && <ErrorState onRetry={() => void awards.refetch()} />}

      {awards.data?.length === 0 && (
        <EmptyState title="עוד לא נבחרו מצטיינים" description="אפשר לבחור את הראשון למעלה." />
      )}

      {awards.data && awards.data.length > 0 && (
        <Table>
          <thead>
            <Tr>
              <Th>חודש</Th>
              <Th>סוג</Th>
              <Th>מי</Th>
              <Th>מצב</Th>
              <Th>
                <span className="sr-only">פעולות</span>
              </Th>
            </Tr>
          </thead>
          <tbody>
            {awards.data.filter((award) => !undo.isPending(award.id)).map((award) => (
              <Tr key={award.id}>
                <Td className="tabular">{formatDate(award.period_month)}</Td>
                <Td>{award.award_types?.name}</Td>
                <Td>
                  {award.employees
                    ? `${award.employees.first_name} ${award.employees.last_name}`
                    : (award.branches?.name ?? '—')}
                </Td>
                <Td>
                  {award.announced_at ? (
                    <StatusBadge tone="good">הוכרז</StatusBadge>
                  ) : (
                    <StatusBadge tone="warn">טיוטה</StatusBadge>
                  )}
                </Td>
                <Td>
                  <div className="flex flex-wrap items-center gap-1">
                    {!award.announced_at && channels.length > 0 && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          const channel = channels[0];
                          if (!channel) return;
                          confirm.ask({
                            title: 'הכרזה בערוץ',
                            tone: 'warn',
                            body: (
                              <p className="m-0">
                                ההודעה תגיע לכל מי שבערוץ "{channel.name}", ואי אפשר למחוק
                                אותה אחר כך.
                              </p>
                            ),
                            confirmLabel: 'שליחת ההכרזה',
                            onConfirm: () =>
                              run(() =>
                                mutations.announceAward.mutateAsync({
                                  id: award.id,
                                  channelId: channel.id,
                                }),
                              ),
                          });
                        }}
                      >
                        <Megaphone size={18} strokeWidth={1.75} aria-hidden="true" />
                        הכרזה בערוץ
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      aria-label="מחיקה"
                      onClick={() => {
                        undo.remove({
                          id: award.id,
                          message: 'המצטיין הוסר',
                          perform: () => mutations.removeAward.mutateAsync(award.id),
                        });
                      }}
                    >
                      <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {confirm.dialog}
    </AdminPage>
  );
}
